import { isDefined, isUndefined } from 'angular';

import { ChanEnum, Channel, ChannelDirection, ChannelType, isString, MediaType, MixableChannel, OpeningChannel } from '@contact/upgrade';

/**
 * channel rules priorities
 */
const channelPriorities: ChannelType[] = [
  ChannelType.PHONE,
  ChannelType.UNITARY_PHONE,
  ChannelType.VIDEO,
  ChannelType.CHAT,
  ChannelType.CHAT_IADVIZE,
  ChannelType.MAIL,
  ChannelType.BASKET,
  ChannelType.SOCIAL_NETWORK,
  ChannelType.VISIO,
];

/**
 * channel type display ordering
 */
const channelDisplayOrder: ChannelType[] = [
  ChannelType.PHONE,
  ChannelType.UNITARY_PHONE,
  ChannelType.MAIL,
  ChannelType.BASKET,
  ChannelType.CHAT,
  ChannelType.CHAT_IADVIZE,
  ChannelType.SOCIAL_NETWORK,
  ChannelType.VISIO,
];

/**
 * channel direction display ordering
 */
const directionDisplayOrder: ChannelDirection[] = [
  ChannelDirection.INCOMING,
  ChannelDirection.OUTGOING,
  ChannelDirection.BOTH,
  ChannelDirection.NOTSET,
];

export class ChannelUtil {
  static INCOMING_PHONE: Channel = ChanEnum.In.Phone;
  static OUTGOING_PHONE: Channel = ChanEnum.Out.Phone;
  static OUTGOING_UNITARY_PHONE: Channel = {
    type: ChannelType.UNITARY_PHONE,
    direction: ChannelDirection.OUTGOING,
  };
  static INCOMING_CHAT: Channel = ChanEnum.In.Chat;
  static INCOMING_CHATBOT: Channel = ChanEnum.In.Chatbot;
  static INCOMING_CHAT_IADVIZE: Channel = ChanEnum.In.ChatIAdvise;
  static INCOMING_BASKET: Channel = {
    type: ChannelType.BASKET,
    direction: ChannelDirection.INCOMING,
  };
  static INCOMING_SOCIAL_NETWORK: Channel = ChanEnum.In.SocialNetwork;
  static INCOMING_MAIL: Channel = ChanEnum.In.Mail;
  static OUTGOING_MAIL: Channel = ChanEnum.Out.Mail;
  static OUTGOING_VISIO: Channel = ChanEnum.Out.Visio;

  /**
   * compare two channels and check strict equality
   * @param {Channel} c1
   * @param {Channel} c2
   */
  static equals(c1: Channel, c2: Channel): boolean {
    return c1.type === c2.type && c1.direction === c2.direction;
  }

  /**
   * compare two channels and check egality
   * @param {Channel} c1
   * @param {Channel} c2
   */
  static areSame(c1: Channel, c2: Channel): boolean {
    return (
      c1.type === c2.type &&
      (c1.direction === c2.direction || c1.direction === ChannelDirection.BOTH || c2.direction === ChannelDirection.BOTH)
    );
  }

  /**
   * Check if given list channels contains expected value
   * @param channels
   * @param expectedChannel
   */
  static hasChannel(channels: Channel[], expectedChannel: Channel): boolean {
    return channels.some(c => ChannelUtil.equals(c, expectedChannel));
  }

  /**
   * Returns channel index in a given channels list
   * @param channels
   * @param channel
   */
  static channelIndex(channels: Channel[], channel: Channel): number {
    return channels.findIndex(c => ChannelUtil.equals(c, channel));
  }

  /**
   * Check if given list channels contains expected type hannel value
   * @param channels
   * @param expectedChannelType
   */
  static hasChannelType(channels: Channel[], expectedChannelType: ChannelType): boolean {
    return channels.some(c => c.type === expectedChannelType);
  }

  /**
   * deserialize channel object
   */
  static from(type: string, direction: string): Channel;
  static from(channelStr: string): Channel | undefined;
  static from(chanOrType: string, dir?: string): Channel | undefined {
    // if dir is not empty use chanOrType as type
    if (!!dir) {
      return {
        type: ChannelType[chanOrType],
        direction: ChannelDirection[dir],
      };
    }
    // otherwise use chanOrType as channel
    let parts: string[] = chanOrType.split('_');
    let type: ChannelType = ChannelType[parts[0]];

    if (parts[1] === 'IADVIZE') {
      type = ChannelType.CHAT_IADVIZE;
    }

    if (isUndefined(type) && parts.length === 3) {
      type = ChannelType[parts[0] + '_' + parts[1]];
    }

    if (isUndefined(type)) {
      type = ChannelType[parts[1]];
    }

    let direction: ChannelDirection = ChannelDirection[parts[1]];
    if (isUndefined(direction)) {
      direction = ChannelDirection[parts[0]];
    }
    if (isUndefined(direction)) {
      direction = ChannelDirection[parts[parts.length - 1]];
    }

    if (!isDefined(type) || !isDefined(direction)) {
      return undefined;
    }
    return {
      type: type,
      direction: direction,
    };
  }

  /**
   * serialize channel object
   */
  static toString(channel: Channel): string;
  static toString(type: ChannelType, direction: ChannelDirection): string;
  static toString(channelOrType: Channel | ChannelType, direction?: ChannelDirection): string {
    if (ChannelUtil.isMediaType(channelOrType) && !!direction) {
      return `${channelOrType}_${direction}`;
    }
    if (ChannelUtil.isChannel(channelOrType)) {
      return `${channelOrType.type}_${channelOrType.direction}`;
    }
    return `${ChannelUtil.toString(ChanEnum.NotSet)}`;
  }

  static toLabel(channel: Channel): string;
  static toLabel(type: ChannelType, direction: ChannelDirection): string;
  static toLabel(channelOrType: Channel | ChannelType, direction?: ChannelDirection): string {
    if (ChannelUtil.isMediaType(channelOrType) && !!direction) {
      return `commons:basic.channels.${ChannelUtil.toString(channelOrType, direction)}`;
    }
    if (ChannelUtil.isChannel(channelOrType)) {
      return `commons:basic.channels.${ChannelUtil.toString(channelOrType)}`;
    }
    return '';
  }

  static toLabelType(channel: Channel): string;
  static toLabelType(type: ChannelType, direction: ChannelDirection): string;
  static toLabelType(channelOrType: Channel | ChannelType, direction?: ChannelDirection): string {
    if (ChannelUtil.isMediaType(channelOrType) && !!direction) {
      const type = `${channelOrType}`;
      return `commons:basic.channels.${type.toUpperCase()}`;
    }
    if (ChannelUtil.isChannel(channelOrType)) {
      const type = `${channelOrType.type}`;
      return `commons:basic.channels.${type.toUpperCase()}`;
    }
    return '';
  }

  static toIconType(channel: Channel): string;
  static toIconType(type: ChannelType, direction: ChannelDirection): string;
  static toIconType(channelOrType: Channel | ChannelType, direction?: ChannelDirection): string {
    let type = MediaType.NOTSET;
    if (ChannelUtil.isMediaType(channelOrType) && !!direction) {
      const channel = ChannelUtil.from(channelOrType, direction);
      type = channel.type;
    } else if (ChannelUtil.isChannel(channelOrType)) {
      type = channelOrType.type;
    }
    switch (type) {
      case MediaType.PHONE:
        return 'cng-ico_appel';
      case MediaType.UNITARY_PHONE:
        return 'cng-ico_appel';
      case MediaType.MAIL:
        return 'cng-ico_mail';
      case MediaType.CHAT:
        return 'cng-ico_chat2';
      case MediaType.BASKET:
        return 'cng-ico_banette';
      case MediaType.VISIO:
        return 'cng-ico_visio';
      case MediaType.SMS:
        return 'cng-ico_SMS';
      default:
        return '';
    }
  }

  /**
   * compare given channel types priorities
   * @param {ChannelType} t1
   * @param {ChannelType} t2
   * @returns {number} a positive value if c1 is higher priority than c2,
   *  otherwise a null value is c1 and c2 have same priorities, and a negative value else.
   */
  static comparePriorities(t1: ChannelType, t2: ChannelType): number {
    let t1Index = channelPriorities.indexOf(t1);
    let t2Index = channelPriorities.indexOf(t2);
    if (t1Index < 0 || t2Index < 0) {
      return t1Index;
    }
    return t2Index - t1Index;
  }

  /**
   * compare given channel order
   * @param {Channel} c1
   * @param {Channel} c2
   */
  static compareOrder(c1: Channel, c2: Channel): number {
    let c1Index = channelDisplayOrder.indexOf(c1.type);
    let c2Index = channelDisplayOrder.indexOf(c2.type);
    if (c1Index === c2Index) {
      return ChannelUtil.compareDirectionOrder(c1.direction, c2.direction);
    }
    if (c1Index < 0) {
      return -1;
    }
    if (c2Index < 0) {
      return 1;
    }
    return c1Index > c2Index ? 1 : -1;
  }

  /**
   * compare given direction order
   * @param {ChannelDirection} d1
   * @param {ChannelDirection} d2
   */
  static compareDirectionOrder(d1: ChannelDirection, d2: ChannelDirection): number {
    let d1Index = directionDisplayOrder.indexOf(d1);
    let d2Index = directionDisplayOrder.indexOf(d2);
    if (d1Index < 0) {
      return -1;
    }
    if (d2Index < 0) {
      return 1;
    }
    return d1Index > d2Index ? 1 : -1;
  }

  /**
   * check if the given channel is part of the mixable channel list
   * @param {MixableChannel} channelMixable
   * @param {Channel} expectedChannel
   */
  static isMixableWith(channelMixable: MixableChannel, expectedChannel: Channel): boolean {
    return (
      channelMixable.mixableChannels.filter(c => c.type === expectedChannel.type && c.direction === expectedChannel.direction).length > 0
    );
  }

  /**
   * check if the given channel is part of the opening channel list
   * @param {OpeningChannel} channelOpening
   * @param {Channel} expectedChannel
   */
  static isOpeningWith(channelOpening: OpeningChannel, expectedChannel: Channel): boolean {
    return (
      channelOpening.openingChannels.filter(c => c.type === expectedChannel.type && c.direction === expectedChannel.direction).length === 0
    );
  }

  static isMediaType(channelOrType: Channel | MediaType): channelOrType is MediaType {
    return isString(channelOrType);
  }

  static isChannel(channelOrType: Channel | MediaType): channelOrType is Channel {
    return !isString(channelOrType) && 'type' in channelOrType && 'direction' in channelOrType;
  }
}
